import {createSelector} from '@reduxjs/toolkit';

import {
	olivioBaseOptions,
	olivioBracketOptions,
	olivioCandelabraPoleHeightOptions, olivioCandelabraTopOptions,
	olivioDesignOptions, olivioFloracionPoleDiameterOptions,
	olivioFloracionPoleHeightOptions, olivioFloracionWoodCylindricalPoleHeightOptions,
	olivioNumberOfHeadsOptions, olivioSecondDoorOptions, olivioSeparateBuriedBaseOptions,
	olivioSistemaPoleHeightOptions, olivioFuseBoxOptions, olivioBuriedBaseSizeOptions, olivioPoleMaterialOptions
} from './olivioModel';
import {selectLanguage, selectProductData, selectSpcAttributes, selectSpcConfiguration} from '../selectors';
import {getValueOfFeature, isDecided} from '../util/values';
import {getProductData} from '../util/getProductData';



export const selectOlivioDesign = state => state.olivio.data.design;
export const selectOlivioDesignIsSatisfied = state => state.olivio.data.design !== null;
export const selectOlivioDesignData = createSelector(
	[selectOlivioDesign],
	(design) => olivioDesignOptions.find(option => option.id === design)
);
export const selectOlivioDesignIsSistema = state => state.olivio.data.design === 'sistema';
export const selectOlivioDesignIsFloracion = state => state.olivio.data.design === 'floracion';
export const selectOlivioDesignIsCandelabra = state => state.olivio.data.design === 'candelabra';
export const selectOlivioDesignIsBollard = state => state.olivio.data.design === 'bollard';
export const selectOlivioDesignIsUniversal = state => state.olivio.data.design === 'universal';

export const selectOlivioPoleHeight = state => state.olivio.data.general.poleHeight;
export const selectOlivioPoleHeightIsSatisfied = state => state.olivio.data.general.poleHeight !== null;

export const selectOlivioOffsetFromTop = createSelector([selectOlivioDesign], (design) => (design === 'floracion' ? 0.65 : 0.4));
export const selectOlivioOffsetFromBottom = state => 3;

export const selectOlivioNumberOfHeads = state => state.olivio.data.general.numberOfHeads;
export const selectOlivioNumberOfHeadsIsSatisfied = state => state.olivio.data.general.poleHeight !== null;
export const selectOlivioMaxNumberOfHeads = state => state.olivio.data.general.maxNumberOfHeads;
export const selectOlivioBracket = state => state.olivio.data.general.bracket;
export const selectOlivioBracketIsSatisfied = state => state.olivio.data.general.bracket !== null;
export const selectOlivioPoleMaterial = state => state.olivio.data.general.poleMaterial;
export const selectOlivioPoleMaterialIsSatisfied = state => state.olivio.data.general.poleMaterial !== null;
export const selectOlivioWoodType = state => state.olivio.data.general.woodType;
export const selectOlivioWoodTypeIsSatisfied = state => state.olivio.data.general.woodType !== null;
export const selectOlivioPoleDiameter = state => state.olivio.data.general.poleDiameter;
export const selectOlivioPoleDiameterIsSatisfied = state => state.olivio.data.general.poleDiameter !== null;
export const selectOlivioBase = state => state.olivio.data.general.base;
export const selectOlivioBaseIsSatisfied = state => state.olivio.data.general.base !== null;
export const selectOlivioSeparateBuriedBase = state => state.olivio.data.general.separateBuriedBase;
export const selectOlivioSeparateBuriedBaseIsSatisfied = state => state.olivio.data.general.separateBuriedBase !== null;
export const selectOlivioSecondDoor = state => state.olivio.data.general.secondDoor;
export const selectOlivioSecondDoorIsSatisfied = state => state.olivio.data.general.secondDoor !== null;
export const selectOlivioFuseBox = state => state.olivio.data.general.fuseBox;
export const selectOlivioFuseBoxIsSatisfied = state => state.olivio.data.general.fuseBox !== null;
export const selectOlivioBuriedBaseSize = state => state.olivio.data.general.buriedBaseSize;
export const selectOlivioBuriedBaseSizeIsSatisfied = state => state.olivio.data.general.buriedBaseSize !== null;

export const selectOlivioHousingColourInlay = state => state.olivio.data.general.housingColourInlay;
export const selectOlivioHousingColourInlayIsSatisfied = state => state.olivio.data.general.housingColourInlay !== null;
export const selectOlivioHousingColourPole = state => state.olivio.data.general.housingColourPole;
export const selectOlivioHousingColourPoleIsSatisfied = state => state.olivio.data.general.housingColourPole !== null;

export const selectOlivioCandelabraTop = state => state.olivio.data.general.candelabraTop;
export const selectOlivioCandelabraTopIsSatisfied = state => state.olivio.data.general.candelabraTop !== null;

export const selectOlivioLayout = state => state.olivio.data.general.layout;
export const selectOlivioLayoutIsSatisfied = state => state.olivio.data.general.layout !== null;

export const selectOlivioMaxHeight = state => state.olivio.data.general.heightMax;
export const selectOlivioMinHeight = state => state.olivio.data.general.heightMin;
export const selectOlivioMaxAngle = state => state.olivio.data.general.angleMax;
export const selectOlivioMinAngle = state => state.olivio.data.general.angleMin;

export const selectOlivioSelectedHead = state => state.olivio.data.selectedHead;
export const selectOlivioHeads = state => state.olivio.data.heads;

export const selectHeadTableVisible = state => state.olivio.data.headTableVisible;

export const selectOlivioHeadsHaveCollisions = createSelector(
	[selectOlivioDesign, selectOlivioHeads],
	(design, heads) => (
		(design === 'sistema' || design === 'floracion') &&
		heads &&
		heads.some((head) => head.collisions?.length > 0))
);

export const selectOlivioHeadSectionIsSatisfied = createSelector([selectSpcAttributes, selectOlivioHeadsHaveCollisions], (spcAttributes, hasCollisions) => (
		!hasCollisions &&
		isDecided('root::LE_ColourTemperature_1', spcAttributes) &&
		isDecided('root::LE_LightDistribution_1', spcAttributes) &&
		isDecided('root::LE_DimmingLevel_1', spcAttributes)
	)
);
export const selectOlivioMiscSectionIsSatisfied = createSelector([selectSpcAttributes], (spcAttributes) => (
		isDecided('root::EB_Control_1', spcAttributes) &&
		isDecided('root::EB_ConstantLumenTechnology_1', spcAttributes) &&
		isDecided('root::EB_Zhaga_1', spcAttributes) &&
		isDecided('root::EB_ElectricalProtectionClass_1', spcAttributes)
	)
);
export const selectOlivioNumberOfHeadsValue = createSelector(
	[selectOlivioNumberOfHeads],
	(numberOfHeads) => olivioNumberOfHeadsOptions.find(option => option.id === numberOfHeads + '').number
);

export const selectOlivioPoleHeightValue = createSelector(
	[selectOlivioPoleHeight, selectOlivioDesign],
	(poleHeight, design) => {
		if (design === 'sistema') {
			return olivioSistemaPoleHeightOptions.find(option => option.id === poleHeight + '')?.meter;
		} else if (design === 'candelabra') {
			return olivioCandelabraPoleHeightOptions.find(option => option.id === poleHeight + '')?.meter;
		} else {
			return olivioFloracionPoleHeightOptions.find(option => option.id === poleHeight + '')?.meter;
		}
	}
);

export const selectOlivioSelectedHeadHeight = createSelector(
	[selectOlivioSelectedHead, selectOlivioHeads],
	(selectedHead, heads) => (selectedHead !== null && selectedHead < heads.length ? heads[selectedHead].height : null)
);
export const selectOlivioSelectedHeadAngle = createSelector(
	[selectOlivioSelectedHead, selectOlivioHeads],
	(selectedHead, heads) => (selectedHead !== null && selectedHead < heads.length ? heads[selectedHead].angle : null)
);



// Generate selectors for heads
export function generateOlivioHeadHeightSelector(index) {
	return createSelector([selectOlivioHeads], (heads) => heads[index].height);
}

export function generateOlivioHeadAngleSelector(index) {
	return createSelector([selectOlivioHeads], (heads) => heads[index].angle);
}

export function generateOlivioHeadHousingSizeSelector(index) {
	return createSelector([selectOlivioHeads], (heads) => heads[index].housingSize);
}

export function generateOlivioHeadColourTemperatureSelector(index) {
	return createSelector([selectOlivioHeads], (heads) => heads[index].colourTemperature);
}

export function generateOlivioHeadLightDistributionSelector(index) {
	return createSelector([selectOlivioHeads], (heads) => heads[index].lightDistribution);
}

export function generateOlivioHeadTypeSelector(index) {
	return createSelector([selectOlivioHeads], (heads) => heads[index].type);
}

export function generateOlivioAccessoriesSelector(index) {
	return createSelector([selectOlivioHeads], (heads) => heads[index].accessories);
}

export function generateOlivioHeadGoboApertureSelector(index) {
	return createSelector([selectOlivioHeads], (heads) => heads[index].goboAperture);
}



// special selectors for olivio SPC values (will change when spc model is done)
export const selectOlivioColourTemperature = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('root::LE_ColourTemperature_1', spcConfiguration)
	)
);
export const selectOlivioLightDistribution = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('root::LE_LightDistribution_1', spcConfiguration)
	)
);
export const selectOlivioHousingSize = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('root::HS_HousingSize', spcConfiguration)
	)
);
export const selectOlivioHousingColour = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('root::HS_HousingColour', spcConfiguration)
	)
);
export const selectOlivioHousingColourHexValue = createSelector([selectOlivioHousingColour, selectProductData], (value, productData) => {
		const attributeProductData = getProductData('root::HS_HousingColour', productData);
		return attributeProductData?.values?.find(val => val.valueId === value)?.displayColour;
	}
);
export const selectOlivioHousingColourInlayHexValue = createSelector([selectOlivioHousingColourInlay, selectProductData], (value, productData) => {
		const attributeProductData = getProductData('root::HS_HousingColour', productData);
		return attributeProductData?.values?.find(val => val.valueId === value)?.displayColour;
	}
);
export const selectOlivioHousingColourPoleHexValue = createSelector([selectOlivioHousingColourPole, selectProductData], (value, productData) => {
		const attributeProductData = getProductData('root::HS_HousingColour', productData);
		return attributeProductData?.values?.find(val => val.valueId === value)?.displayColour;
	}
);
export const selectOlivioReflectorColour = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('root::LE_ReflectorColour_1', spcConfiguration)
	)
);
export const selectOlivioDesignLabel = createSelector([selectOlivioDesign, selectLanguage], (design, lang) => (
		olivioDesignOptions.find((designOption) => designOption.id === design).label[lang]
	)
);
export const selectOlivioAccessories = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('root::LE_Accessories_1', spcConfiguration)
	)
);
export const selectOlivioBaseDescription = createSelector([selectLanguage, selectOlivioBase], (lang, base) => {
		const baseOptions = olivioBaseOptions.find((option) => option.id === base);
		return baseOptions.description[lang] ?? '';
	}
);
export const selectOlivioSeparateBuriedBaseNumber = createSelector([selectLanguage, selectOlivioSeparateBuriedBase, selectOlivioPoleHeight], (lang, base, poleHeight) => {
		const options = olivioSeparateBuriedBaseOptions.find((option) => option.id === base);
		return options.poleHeights[poleHeight].number ?? '';
	}
);
export const selectOlivioSeparateBuriedBaseHeight = createSelector([selectLanguage, selectOlivioSeparateBuriedBase, selectOlivioPoleHeight], (lang, base, poleHeight) => {
		const options = olivioSeparateBuriedBaseOptions.find((option) => option.id === base);
	return options.poleHeights[poleHeight].height ?? '';
	}
);
export const selectOlivioSeparateBuriedBasePrice = createSelector([selectLanguage, selectOlivioSeparateBuriedBase, selectOlivioPoleHeight], (lang, base, poleHeight) => {
		const options = olivioSeparateBuriedBaseOptions.find((option) => option.id === base);
		return options.poleHeights[poleHeight].price ?? '';
	}
);
export const selectOlivioSeparateBuriedBaseDescription = createSelector([selectLanguage, selectOlivioSeparateBuriedBase], (lang, base) => {
		const baseOptions = olivioSeparateBuriedBaseOptions.find((option) => option.id === base);
		return baseOptions.description[lang] ?? '';
	}
);
export const selectOlivioBaseNumber = createSelector([selectLanguage, selectOlivioBase], (lang, base) => {
		const baseOptions = olivioBaseOptions.find((option) => option.id === base);
		return baseOptions.number ?? '';
	}
);
export const selectOlivioPoleDescription = createSelector([selectLanguage, selectOlivioDesign, selectOlivioPoleMaterial], (lang, design, material) => {
		if (material === 'steel') {
			const designOptions = olivioDesignOptions.find((option) => option.id === design);
			return designOptions.pole[lang] ?? '';
		} else {
			return olivioPoleMaterialOptions.find((option) => option.id === material).label[lang] ?? '';
		}
	}
);
export const selectOlivioBracketDescription = createSelector([selectLanguage, selectOlivioBracket], (lang, bracket) => {
		const bracketOptions = olivioBracketOptions.find((option) => option.id === bracket);
		return bracketOptions.description[lang] ?? '';
	}
);

export const selectOlivioBracketNumber = createSelector([selectOlivioDesign, selectOlivioBracket, selectOlivioHousingSize],
	(design, bracket, housingSize) => {
		if (design === 'sistema') {
			const bracketOption = olivioBracketOptions.find((option) => option.id === bracket);
			if (bracket === 'sistema3') {
				return bracketOption.number[housingSize];
			} else {
				return bracketOption.number;
			}
		}
		return '';
	});
export const selectOlivioBracketPrice = createSelector([selectOlivioDesign, selectOlivioBracket, selectOlivioHousingSize],
	(design, bracket, housingSize) => {
		if (design === 'sistema') {
			const bracketOption = olivioBracketOptions.find((option) => option.id === bracket);
			if (bracket === 'sistema3') {
				return bracketOption.price[housingSize];
			} else {
				return bracketOption.price;
			}
		}
		return '';
	});

// design: sistema, floracion, candelabra
// material null, wood
// height: height in m
// diameter: diameter in mm
export const selectOlivioPoleNumber = createSelector([selectOlivioDesign, selectOlivioPoleMaterial, selectOlivioPoleHeightValue, selectOlivioCandelabraTop, selectOlivioBase],
	(design, material, height, top, base) => {

		if (material === 'steel') {
			if (design === 'candelabra') {
				const candelabraOption = olivioCandelabraPoleHeightOptions.find(option => option.meter === height);
				const topOption = olivioCandelabraTopOptions.find(option => option.id === top);
				if (candelabraOption && candelabraOption.number && topOption.number) {
					const topBaseNumber = '' + (parseInt(topOption.number, 10) + (base !== 'buriedbase' ? 4 : 0));
					return 'SX 196 ' + candelabraOption.number[height] + topBaseNumber + '-9';
				}
				return '';
			}

			// Handle Sistema design
			if (design === 'sistema') {
				const sistemaOption = olivioSistemaPoleHeightOptions.find(option => option.meter === height);
				console.log('sistema', olivioSistemaPoleHeightOptions, sistemaOption, height);
				if (sistemaOption && sistemaOption.number) {
					return sistemaOption.number;
				}
				return '';
			}

			// Handle Floracion design
			if (design === 'floracion') {
				const floracionOption = olivioFloracionPoleHeightOptions.find(option => option.meter === height);
				if (floracionOption && floracionOption.number) {
					return floracionOption.number;
				}
				return '';
			}
		}

		if (material === 'wood_cylindrical') {
			return '';
		}
		if (material === 'wood_conical') {
			return '';
		}

		// Return null for any other cases
		return '';
	}
);

// select base value, but return 'custom' when not all heads have the same value
export const selectOlivioColourTemperatureInclCustom = createSelector([selectOlivioColourTemperature, selectOlivioHeads], (colourTemperature, heads) => (
		// if all heads properties match the general housingSize, return it, otherwise return "custom"
		colourTemperature === 'custom' || heads.every(head => head.colourTemperature === colourTemperature) ? colourTemperature : 'custom'
	)
);
export const selectOlivioLightDistributionInclCustom = createSelector([selectOlivioLightDistribution, selectOlivioHeads], (lightDistribution, heads) => (
		// if all heads properties match the general value, return it, otherwise return "custom"
		lightDistribution === 'custom' || heads.every(head => head.lightDistribution === lightDistribution) ? lightDistribution : 'custom'
	)
);
export const selectOlivioAccessoriesInclCustom = createSelector([selectOlivioAccessories, selectOlivioHeads], (accessories, heads) => (
		// if all heads properties match the general value, return it, otherwise return "custom"
		accessories === 'custom' || heads.every(head => head.accessories === accessories) ? accessories : 'custom'
	)
);
export const selectOlivioHousingSizeInclCustom = createSelector([selectOlivioHousingSize, selectOlivioHeads], (housingSize, heads) =>
	// if all heads properties match the general housingSize, return it, otherwise return "custom"
	((housingSize === 'custom' || heads.every(head => head.housingSize === housingSize)) ? housingSize : 'custom')
);

// selector for significant differences of head configuration compared to base
export function generateOlivioHeadIsDifferentFromBase(index) {
	return createSelector([
		selectOlivioHeads,
		selectOlivioHousingSize,
		selectOlivioHousingColour,
		selectOlivioColourTemperature,
		selectOlivioLightDistribution,
		selectOlivioReflectorColour,
		selectOlivioAccessories
	], (heads, housingSize, housingColour, colourTemperature, lightDistribution, reflectorColour, accessories) => {
		const head = heads[index];

		const headData = {
			type: head.type,
			housingSize: head.housingSize,
			colourTemperature: head.colourTemperature,
			lightDistribution: head.lightDistribution,
			goboAperture: head.goboAperture,
			// accessories: head.accessories,
			// housingColour: head.housingColour
			// reflectorColor: head.reflectorColour,
		};
		const baseHeadData = {
			type: 'luminaire', // only luminaries in base
			housingSize: housingSize,
			colourTemperature: colourTemperature,
			lightDistribution: lightDistribution,
			goboAperture: 'normal', // default for base head
			// accessories: accessories,
			// housingColour: housingColour,
			// reflectorColor: reflectorColour,
		};

		return JSON.stringify(headData) !== JSON.stringify(baseHeadData);
	});
}

export const selectGroupedHeads = createSelector(
	[selectOlivioHeads],
	(heads) => {
		let groupIndex = 0;
		const groupedHeads = heads.filter(head => head.active).reduce((groups, head, index) => {
			// Create a unique key using properties of the head
			const headData = {
				type: head.type,
				housingSize: head.housingSize,
				// housingColour: head.housingColour,
				colourTemperature: head.colourTemperature,
				lightDistribution: head.lightDistribution,
				// reflectorColor: head.reflectorColor,
				// accessories: head.accessories,
				goboAperture: head.goboAperture
			};

			const key = JSON.stringify(headData);

			if (!groups[key]) {
				groups[key] = {
					head: headData,
					groupIndex: groupIndex,
					instanceId: 'olivioHeadGroup_' + groupIndex,
					indexes: []
				};
				groupIndex++;
			}
			groups[key].indexes.push(index);

			return groups;
		}, {});

		// Convert the grouped object into an array of group values
		return Object.entries(groupedHeads).map(([key, value]) => value);
	});

export const selectOlivioCandelabraTopAngles = createSelector([selectOlivioCandelabraTop], (topId) => {
		const options = olivioCandelabraTopOptions.find((option) => option.id === topId);
		return options.angles ?? [];
	}
);
export const selectOlivioCandelabraTopLabel = createSelector([selectLanguage, selectOlivioCandelabraTop], (lang, topId) => {
		const options = olivioCandelabraTopOptions.find((option) => option.id === topId);
		return options.label[lang] ?? '';
	}
);
export const selectOlivioSecondDoorLabel = createSelector([selectLanguage, selectOlivioSecondDoor], (lang, secondDoor) => {
		const options = olivioSecondDoorOptions.find((option) => option.id === secondDoor);
		return options?.label[lang] ?? '';
	}
);
export const selectOlivioSecondDoorDescription = createSelector([selectLanguage, selectOlivioSecondDoor, selectOlivioDesign], (lang, secondDoor, design) => {
		const options = olivioSecondDoorOptions.find((option) => option.id === secondDoor);
		return options?.designs[design][lang] ?? '';
	}
);
export const selectOlivioFuseBoxLabel = createSelector([selectLanguage, selectOlivioFuseBox], (lang, fuseBox) => {
		const options = olivioFuseBoxOptions.find((option) => option.id === fuseBox);
		return options?.label[lang] ?? '';
	}
);
export const selectOlivioFuseBoxNumber = createSelector([selectLanguage, selectOlivioFuseBox], (lang, fuseBox) => {
		const options = olivioFuseBoxOptions.find((option) => option.id === fuseBox);
		return options?.number ?? '';
	}
);
export const selectOlivioFuseBoxDescription = createSelector([selectLanguage, selectOlivioFuseBox], (lang, fuseBox) => {
		const options = olivioFuseBoxOptions.find((option) => option.id === fuseBox);
		return options?.description[lang] ?? '';
	}
);
export const selectOlivioBuriedBaseSizeSize = createSelector([selectLanguage, selectOlivioBuriedBaseSize], (lang, base, size) => {
		const options = olivioBuriedBaseSizeOptions.find((option) => option.id === size);
		return options.size ?? '';
	}
);
export const selectOlivioDiameter = createSelector([selectLanguage, selectOlivioDesign, selectOlivioPoleHeight, selectOlivioHousingSize, selectOlivioPoleMaterial, selectOlivioPoleDiameter], (lang, design, height, housingSize, material, diameter) => {
		let poleOptions;
		if (design === 'sistema') {
			poleOptions = olivioSistemaPoleHeightOptions;
		}
		if (design === 'floracion') {
			poleOptions = material === 'steel' ? olivioFloracionPoleHeightOptions : olivioFloracionWoodCylindricalPoleHeightOptions;
		}
		if (design === 'candelabra') {
			poleOptions = olivioCandelabraPoleHeightOptions;
		}

		const poleOption = poleOptions.find((option) => option.meter === height);

		if (design === 'floracion' && material !== 'steel') {
			return material === 'wood_cylindrical' ? diameter : 140;
		}
		if (design === 'floracion' && material === 'steel') {
			return poleOption.diameter[housingSize];
		}
		return poleOption.diameter;

	}
);
export const selectOlivioWoodPoleNumber = createSelector([selectOlivioDesign, selectOlivioHousingSize, selectOlivioPoleHeight, selectOlivioDiameter, selectOlivioPoleMaterial, selectOlivioNumberOfHeads, selectOlivioSecondDoor, selectOlivioBase],
	(design, size, height, diameter, material, numberOfHeads, doors, base) => {


		const designSizeCode = {
			sistema: {
				OlivioGrande: '0',
				OlivioMedio: '0',
				OlivioPiccolo: '0',
			},
			floracion:  {
				OlivioGrande: '1',
				OlivioMedio: '1',
				OlivioPiccolo: '2',
			}
		};
		const heightCode = {
			3: '03',
			4: '04',
			5: '05',
			6: '06',
			7: '07',
			8: '08',
			9: '09',
			10: '10',
			11: '11',
			12: '12',
		};
		const diameterCode = {
			140: '0',
			168: '1',
			220: '2'
		};
		const materialCode = {
			steel: '',
			wood_cylindrical: '0',
			wood_conical: '1'
		};
		const numberOfHeadsCode = ''+numberOfHeads; // 1-8
		const doorsCode = {
			none: '1',
			secondDoor: '2',
		};
		const baseCode = {
			FlangePlateDE: 1,
			buriedbase: '2',
			FlangePlateFR: '3',
		};
		const runningNumber = '';

		return 'SX 16' + designSizeCode[design][size] + ' ' + heightCode[height] + '-W-' + diameterCode[diameter] + materialCode[material] + numberOfHeadsCode + doorsCode[doors] + baseCode[base] + runningNumber;
	}
);
